import { Suspense, lazy, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Network, Search, X } from "lucide-react";
import api from "../lib/api";
import useViewportWidth from "../hooks/useViewportWidth";
import { FILTER_KINDS, TABS, architectureError, kindLabel, moduleLabel, readParams, searching, writeParams, CONFIDENCE_LABELS } from "../lib/architecture";
import { panelMode } from "../lib/quickPanel";
import { SnapshotBar } from "../components/architecture/ArchitectureBits";
import NodePanel from "../components/architecture/NodePanel";
import { DomainPanel, OverviewTab } from "../components/architecture/ArchitectureOverview";
import { CoverageTab, DomainsTab, LoadError, ResultList, UnknownsTab } from "../components/architecture/ArchitectureTabs";
import VersionsTab from "../components/architecture/VersionsTab";
import ExportMenu from "../components/architecture/ExportMenu";
import { useAuth } from "../contexts/AuthContext";
import { hasPermission } from "../lib/permissions";
import { Skeleton } from "../components/ui/skeleton";

// The graph library (cytoscape + dagre) is its own chunk, loaded only when the explorer opens.
const LineageExplorer = lazy(() => import("../components/architecture/LineageExplorer"));
const ExplorerStart = lazy(() => import("../components/architecture/LineageExplorer").then((m) => ({ default: m.ExplorerStart })));

const SELECT = "h-10 rounded-md border border-slate-300 bg-white px-2 text-sm text-slate-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-jamora [@media(pointer:coarse)]:h-11";

// Cartographie du système (lot CRM-3): a published snapshot of the repository's map, consulted by domain, by search or by element, with the proof
// (file and line) and the confidence of every relation. Read only; nothing is scanned here and no code is stored in what is shown.
export default function Architecture() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const params = readParams(searchParams.toString());
  const width = useViewportWidth();
  const mode = panelMode(width);
  const pushed = useRef(false);
  const silentReload = useRef(false);
  const [status, setStatus] = useState({ phase: "loading" });
  const [overview, setOverview] = useState(null);
  const [results, setResults] = useState({ phase: "idle", items: [], total: 0 });
  const [input, setInput] = useState(params.q);
  const [attempt, setAttempt] = useState(0);
  // What changes INSIDE the open panel (another element, the schema/list choice) rewrites the address in place: going through the router would replace
  // the history entry that the overlay registered and Back would then unwind the wrong entries.
  const [inPanel, setInPanel] = useState({});
  const snapshot = params.snapshot;
  const nodeId = "node" in inPanel ? inPanel.node : params.node;
  const domainId = "domain" in inPanel ? inPanel.domain : params.domain;
  const view = inPanel.view || params.view;

  const patch = useCallback((changes, replace = true) => {
    setSearchParams(writeParams(readParams(window.location.search), changes), { replace });
  }, [setSearchParams]);

  // Snapshots, then the overview of the one consulted.
  useEffect(() => {
    let live = true;
    // A refresh after an update or a rollback keeps the page (and the Versions tab's own state) on screen instead of flashing a skeleton.
    if (!silentReload.current) setStatus({ phase: "loading" });
    silentReload.current = false;
    api.get("/architecture/status")
      .then(async ({ data }) => {
        if (!live) return;
        if (!data.snapshot) { setStatus({ phase: "empty" }); return; }
        const { data: ov } = await api.get("/architecture/overview", { params: snapshot ? { snapshot } : {} });
        if (live) { setOverview(ov); setStatus({ phase: "ready", data }); }
      })
      .catch((e) => live && setStatus({ phase: "error", message: architectureError(e, "Impossible de charger la cartographie. Réessayez dans quelques instants.") }));
    return () => { live = false; };
  }, [snapshot, attempt]);

  // Typing writes the address after a short pause: the address is the single source of truth (shareable, Back-friendly).
  useEffect(() => {
    if (input === params.q) return undefined;
    const timer = setTimeout(() => patch({ q: input }), 300);
    return () => clearTimeout(timer);
  }, [input]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setInput(params.q); }, [params.q]);

  // Results: a domain on its own is browsed page by page, anything else goes through the search.
  const browsing = searching(params) && !!params.module && params.q.trim().length < 2 && !params.kind && !params.confidence;
  const wantsResults = status.phase === "ready" && searching(params);
  useEffect(() => {
    if (!wantsResults) { setResults({ phase: "idle", items: [], total: 0 }); return undefined; }
    let live = true;
    setResults((r) => ({ ...r, phase: "loading" }));
    const base = snapshot ? { snapshot } : {};
    const request = browsing
      ? api.get("/architecture/module", { params: { ...base, id: params.module, limit: 100 } }).then(({ data }) => ({ items: data.items, total: data.total, truncated: false }))
      : api.get("/architecture/search", { params: { ...base, q: params.q.trim(), ...(params.kind ? { kind: params.kind } : {}), ...(params.module ? { module: params.module } : {}), ...(params.confidence ? { confidence: params.confidence } : {}), limit: 100 } })
        .then(({ data }) => ({ items: data.results, total: data.total, truncated: data.truncated }));
    request
      .then((data) => live && setResults({ phase: "ready", ...data }))
      .catch((e) => live && setResults({ phase: "error", items: [], total: 0, message: architectureError(e, "La recherche a échoué. Réessayez dans quelques instants."), retry: () => setAttempt((n) => n + 1) }));
    return () => { live = false; };
  }, [wantsResults, browsing, params.q, params.kind, params.module, params.confidence, snapshot, attempt]); // eslint-disable-line react-hooks/exhaustive-deps

  const moreBrowse = () => api.get("/architecture/module", { params: { ...(snapshot ? { snapshot } : {}), id: params.module, skip: results.items.length, limit: 100 } })
    .then(({ data }) => setResults((r) => ({ ...r, items: [...r.items, ...data.items], total: data.total }))).catch(() => {});

  // Element detail: a first opening is a history entry (Back closes it), moving from one element to another replaces it.
  const changeInPlace = (changes) => {
    setInPanel((previous) => ({ ...previous, ...changes }));
    const query = writeParams(readParams(window.location.search), changes);
    window.history.replaceState(window.history.state, "", `${window.location.pathname}${query ? `?${query}` : ""}`);
  };
  const openNode = (id) => {
    if (!id || id === nodeId) return;
    if (nodeId) { changeInPlace({ node: id }); return; }
    pushed.current = true;
    patch({ node: id }, false);
  };
  const CLOSED = { node: "", domain: "" };  // explicit: the router's own parameters may still name an element the panel has left in place
  const closeNode = () => {
    setInPanel(CLOSED);
    if (pushed.current) { pushed.current = false; navigate(-1); } else patch({ node: "" });
  };
  useEffect(() => { if (!params.node && !params.domain) { pushed.current = false; setInPanel({}); } }, [params.node, params.domain]);

  // A domain panel: a first opening is a history entry too; moving to another domain rewrites the address in place.
  const openDomain = (id) => {
    if (!id || id === domainId) return;
    if (domainId) { changeInPlace({ domain: id }); return; }
    pushed.current = true;
    patch({ domain: id }, false);
  };
  const closeDomain = () => {
    setInPanel(CLOSED);
    if (pushed.current) { pushed.current = false; navigate(-1); } else patch({ domain: "" });
  };
  // From a domain to one of its elements: the domain panel gives way, the element panel takes its place.
  const nodeFromDomain = (id) => changeInPlace({ domain: "", node: id });
  const browseDomain = () => { const id = domainId; setInPanel(CLOSED); pushed.current = false; patch({ domain: "", module: id, tab: "domains" }, true); };

  // From an element's detail to the explorer, starting at that element: the address changes, so the open panel gives way by itself.
  const exploreFrom = (id) => { setInPanel(CLOSED); pushed.current = false; patch({ node: "", domain: "", tab: "explorer", explore: id }, false); };

  const clear = () => { setInput(""); patch({ q: "", kind: "", confidence: "", module: "" }, false); };
  const snap = status.phase === "ready" ? overview.snapshot : null;
  const showResults = searching(params);

  return (
    <div className="space-y-4" data-testid="architecture-page">
      <div>
        <h2 className="flex items-center gap-2 text-2xl font-semibold text-slate-900"><Network size={22} className="text-jamora" aria-hidden />Cartographie du système</h2>
        <p className="mt-1 max-w-3xl text-sm text-slate-600">Du menu jusqu'au fichier : les pages, les routes, les droits, les données et les événements du CRM, avec la preuve et la confiance de chaque lien.</p>
      </div>

      {status.phase === "loading" ? <div className="space-y-3" data-testid="arch-loading"><Skeleton className="h-10 w-full" /><Skeleton className="h-40 w-full" /></div> : null}
      {status.phase === "error" ? <LoadError message={status.message} onRetry={() => setAttempt((n) => n + 1)} /> : null}
      {status.phase === "empty" ? (
        <div className="rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center" data-testid="arch-no-snapshot">
          <p className="font-medium text-slate-900">Aucun instantané n'est encore publié</p>
          <p className="mx-auto mt-1 max-w-xl text-sm text-slate-600">La cartographie se publie à partir d'un registre produit par le scanner du dépôt. Lancez <code className="rounded bg-slate-100 px-1">python -m architecture.scanner</code>, puis <code className="rounded bg-slate-100 px-1">python scripts/publish_architecture_snapshot.py registre.json --execute</code>.</p>
        </div>
      ) : null}

      {status.phase === "ready" ? (
        <>
          <SnapshotBar snapshot={snap} viewingOld={!!snapshot && !status.data.versions.find((v) => v.snapshot_id === snapshot)?.is_current} lastScan={status.data.last_scan} onLatest={() => patch({ snapshot: "" }, false)} />

          <div className="flex flex-wrap items-center gap-2" role="search">
            <div className="relative min-w-0 flex-1 basis-64">
              <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden />
              <input type="search" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Chercher un nom, un chemin, une route, une collection, un droit…" aria-label="Chercher un élément"
                data-testid="arch-search" className="h-10 w-full rounded-md border border-slate-300 bg-white pl-9 pr-3 text-base text-slate-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-jamora sm:text-sm [@media(pointer:coarse)]:h-11" />
            </div>
            <select aria-label="Nature de l'élément" value={params.kind} onChange={(e) => patch({ kind: e.target.value }, false)} className={SELECT} data-testid="arch-kind">
              <option value="">Toutes les natures</option>
              {FILTER_KINDS.map((k) => <option key={k} value={k}>{kindLabel(k)}</option>)}
            </select>
            {hasPermission(user, "architecture:export") ? <ExportMenu snapshot={snapshot} module={params.module} /> : null}
            <select aria-label="Confiance" value={params.confidence} onChange={(e) => patch({ confidence: e.target.value }, false)} className={SELECT} data-testid="arch-confidence">
              <option value="">Toute confiance</option>
              {Object.entries(CONFIDENCE_LABELS).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
            </select>
          </div>

          {params.module || params.kind || params.confidence || params.q ? (
            <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="arch-chips">
              {params.module ? <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-1 text-slate-800">Domaine : {moduleLabel(params.module)}<button type="button" aria-label="Retirer le domaine" onClick={() => patch({ module: "" }, false)}><X size={12} aria-hidden /></button></span> : null}
              <button type="button" onClick={clear} className="font-medium text-jamora hover:underline" data-testid="arch-clear">Tout effacer</button>
            </div>
          ) : null}

          {showResults ? (
            <ResultList state={results} title={params.module && !params.q ? `Contenu de ${moduleLabel(params.module)}` : "Résultats"} onOpen={openNode}
              onMore={browsing ? moreBrowse : null} hint="Essayez un autre mot, ou retirez un filtre." />
          ) : (
            <div className="space-y-4">
              <div role="tablist" aria-label="Sections" className="flex gap-1 overflow-x-auto border-b border-slate-200">
                {TABS.map(([id, label]) => (
                  <button key={id} role="tab" type="button" aria-selected={params.tab === id} onClick={() => patch({ tab: id }, false)} data-testid={`arch-tab-${id}`}
                    className={`shrink-0 border-b-2 px-3 py-2 text-sm font-medium [@media(pointer:coarse)]:min-h-11 ${params.tab === id ? "border-jamora text-jamora" : "border-transparent text-slate-600 hover:text-slate-900"}`}>{label}</button>
                ))}
              </div>
              {params.tab === "overview" ? <OverviewTab snapshot={snapshot} onPick={openDomain} /> : null}
              {params.tab === "domains" ? <DomainsTab overview={overview} onPick={(id) => patch({ module: id }, false)} /> : null}
              {params.tab === "explorer" ? (
                <Suspense fallback={<Skeleton className="h-96 w-full" data-testid="arch-loading" />}>
                  {params.explore
                    ? <LineageExplorer startId={params.explore} snapshot={snapshot} onOpenNode={openNode} onStart={(id) => patch({ explore: id }, true)} onChange={() => patch({ explore: "" }, false)} />
                    : <ExplorerStart snapshot={snapshot} onStart={(id) => patch({ explore: id }, false)} />}
                </Suspense>
              ) : null}
              {params.tab === "unknowns" ? <UnknownsTab snapshot={snapshot} /> : null}
              {params.tab === "coverage" ? <CoverageTab overview={overview} /> : null}
              {params.tab === "versions" ? (
                <VersionsTab status={status.data} current={overview.snapshot.snapshot_id} from={params.from} to={params.to} onPick={(id) => patch({ snapshot: id, node: "" }, false)}
                  onCompare={(a, b) => patch({ from: a, to: b }, false)} onOpenNode={(id) => openNode(id)} onChanged={() => { silentReload.current = true; setAttempt((n) => n + 1); }} />
              ) : null}
            </div>
          )}
        </>
      ) : null}

      {domainId && status.phase === "ready" ? (
        <DomainPanel domainId={domainId} snapshot={snapshot} mode={mode} onPick={openDomain} onNode={nodeFromDomain} onBrowse={browseDomain} onClose={closeDomain} />
      ) : null}
      {nodeId && status.phase === "ready" ? (
        <NodePanel nodeId={nodeId} snapshot={snapshot} mode={mode} view={view} onView={(v) => changeInPlace({ view: v })} onOpen={openNode} onExplore={exploreFrom} onClose={closeNode} />
      ) : null}
    </div>
  );
}
